import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Controller, useFieldArray } from 'react-hook-form'

import {
  Button,
  Checkbox,
  Fields,
  FormControl,
  IconButton,
  Icons,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Row,
  Select,
  Stack,
  Text,
  useAlert,
  validations,
} from '..'
import { PROVINCES_LIST } from '../../constants'
import { fromAmountField } from '../../utils'
import { openApiFileWithData } from '../../utils/open-api-file'

/**
 * @typedef CryptocurrencyValues
 * @property {string} cryptocurrencyType - Cryptocurrency Type (Bitcoin, Ethereum, etc.)
 * @property {string} walletAddress - Wallet Address
 * @property {string|number} quantity - Quantity
 * @property {string|number} amount - Approx Value
 * @property {string} transactionHash - Transaction Hash (optional)
 * @property {string} notes - Additional Notes (optional)
 */

/** @type {CryptocurrencyValues[]} */
export const defaultCryptocurrencies = [
  {
    cryptocurrencyType: '',
    walletAddress: '',
    quantity: 0,
    amount: 0,
    transactionHash: '',
    notes: '',
  },
]

/**
 * Common cryptocurrency types
 */
export const CRYPTOCURRENCY_TYPES = [
  'Bitcoin (BTC)',
  'Ethereum (ETH)',
  'Litecoin (LTC)',
  'Bitcoin Cash (BCH)',
  'Ripple (XRP)',
  'Cardano (ADA)',
  'Dogecoin (DOGE)',
  'Polkadot (DOT)',
  'Chainlink (LINK)',
  'Other',
]

/**
 * Utility for calculating the total approx value of a list of cryptocurrencies
 *
 * @param {CryptocurrencyValues[]} cryptocurrencies - The list of cryptocurrencies
 * @returns {number} The total approx value
 */
export function calculateSumForCryptocurrencies(cryptocurrencies) {
  return cryptocurrencies.reduce((total, crypto) => {
    const amount = fromAmountField(crypto.amount || '0')

    return total + amount
  }, 0)
}

export function ExchangeInformation({ register, control, formState }) {
  const { errors = {} } = formState

  return (
    <Stack sx={{ mb: 2 }}>
      <Stack sx={{ display: 'flex', mb: 2 }}>
        <Text.H6 sx={{ mb: -2 }}>Exchange/Wallet Information</Text.H6>
        <Text.Subtitle sx={{ mt: -2 }}>
          Please provide information about your cryptocurrency exchange or wallet provider.
        </Text.Subtitle>
      </Stack>

      <Fields.Text
        inputProps={{ 'data-testid': 'exchangeName' }}
        type="text"
        label="Exchange/Wallet Provider Name *"
        error={!!errors?.exchangeName}
        helperText={errors?.exchangeName?.message}
        {...register('exchangeName', {
          required: 'This field is required',
          validate: {
            noSpecialCharacters: (value) => /^[a-zA-Z0-9\s]*$/.test(value) || 'No special characters allowed',
          },
        })}
      />
      <Row>
        <Fields.Text
          inputProps={{ 'data-testid': 'contactEmail' }}
          type="text"
          label="Contact Email *"
          error={!!errors?.contactEmail}
          helperText={errors?.contactEmail?.message}
          {...register('contactEmail', { ...validations.required, ...validations.email })}
        />
        <Fields.Text
          inputProps={{ 'data-testid': 'contactPhone' }}
          type="text"
          label="Contact Phone Number *"
          error={!!errors.contactPhone}
          helperText={errors?.contactPhone?.message}
          {...register('contactPhone', { ...validations.required, ...validations.phoneNumber })}
        />
      </Row>
      <Fields.Text
        inputProps={{ 'data-testid': 'contactName' }}
        type="text"
        label="Contact Name *"
        error={!!errors.contactName}
        helperText={errors?.contactName?.message}
        {...register('contactName', { ...validations.required })}
      />

      <Fields.Text
        inputProps={{ 'data-testid': 'exchangeAddress' }}
        type="text"
        label="Exchange/Wallet Provider Address *"
        error={!!errors.address?.lineOne}
        helperText={errors?.address?.lineOne?.message}
        {...register('address.lineOne', { ...validations.required })}
      />
      <Row>
        <Fields.Text
          inputProps={{ 'data-testid': 'city' }}
          type="text"
          label="City *"
          error={!!errors.address?.city}
          helperText={errors?.address?.city?.message}
          {...register('address.city', { ...validations.required })}
        />
        <Fields.Text
          inputProps={{ 'data-testid': 'province' }}
          type="text"
          label="Province/State *"
          error={!!errors.address?.province}
          helperText={errors?.address?.province?.message}
          {...register('address.province', { ...validations.required })}
        />
        <Fields.Text
          inputProps={{ 'data-testid': 'postalCode' }}
          type="text"
          label="Postal Code/ZIP *"
          error={!!errors.address?.postalCode}
          helperText={errors?.address?.postalCode?.message}
          {...register('address.postalCode', { ...validations.required })}
        />
      </Row>
    </Stack>
  )
}

export function CryptocurrenciesToTransfer({ required = false, register, control, formState }) {
  const { isSubmitting, errors = {} } = formState
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'cryptocurrencies',
  })

  return (
    <Stack sx={{ mb: 2 }}>
      <Text.H6 sx={{ display: 'flex' }}>Cryptocurrencies To Transfer{required && ' *'}</Text.H6>

      {fields.map((field, index) => (
        <Stack key={field.id} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Row>
            <Controller
              control={control}
              name={`cryptocurrencies[${index}].cryptocurrencyType`}
              rules={{ required: 'This field is required' }}
              render={({ field: controllerField }) => (
                <FormControl
                  fullWidth
                  error={!!errors?.cryptocurrencies?.[index]?.cryptocurrencyType}
                  sx={{ minWidth: 200 }}
                >
                  <InputLabel>Cryptocurrency Type *</InputLabel>
                  <Select
                    {...controllerField}
                    label="Cryptocurrency Type *"
                    data-testid={`cryptocurrencies[${index}].cryptocurrencyType`}
                  >
                    {CRYPTOCURRENCY_TYPES.map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors?.cryptocurrencies?.[index]?.cryptocurrencyType && (
                    <Text.Body sx={{ color: 'red', fontSize: '0.75rem', mt: 0.5 }}>
                      {errors.cryptocurrencies[index].cryptocurrencyType.message}
                    </Text.Body>
                  )}
                </FormControl>
              )}
            />
            <Fields.Text
              inputProps={{ 'data-testid': `cryptocurrencies[${index}].walletAddress` }}
              type="text"
              label="Wallet Address *"
              error={!!errors?.cryptocurrencies?.[index]?.walletAddress}
              helperText={errors?.cryptocurrencies?.[index]?.walletAddress?.message}
              {...register(`cryptocurrencies[${index}].walletAddress`, { ...validations.required })}
            />
          </Row>
          <Row>
            <Controller
              control={control}
              name={`cryptocurrencies[${index}].quantity`}
              defaultValue={0}
              rules={{
                required: 'This field is required',
                validate: {
                  positive: (value) => parseFloat(value) > 0 || 'Must be a positive number',
                  min: (value) => parseFloat(value) > 0 || 'Must be greater than 0',
                },
              }}
              render={({ field: { ref, ...fieldProps } }) => (
                <Fields.Text
                  {...fieldProps}
                  inputRef={ref}
                  label="Quantity *"
                  error={!!errors?.cryptocurrencies?.[index]?.quantity}
                  helperText={errors?.cryptocurrencies?.[index]?.quantity?.message}
                  InputProps={{
                    inputComponent: Fields.Amount,
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name={`cryptocurrencies[${index}].amount`}
              defaultValue={0}
              rules={{
                required: 'This field is required',
                validate: {
                  positive: (value) => parseFloat(value) > 0 || 'Must be a positive number',
                  min: (value) => parseFloat(value) >= 1 || 'Must be at least $1',
                },
              }}
              render={({ field: { ref, ...fieldProps } }) => (
                <Fields.Text
                  {...fieldProps}
                  inputRef={ref}
                  label="Approx Total Value *"
                  name={`cryptocurrencies[${index}].amount`}
                  error={!!errors?.cryptocurrencies?.[index]?.amount}
                  helperText={errors?.cryptocurrencies?.[index]?.amount?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    inputComponent: Fields.Amount,
                  }}
                />
              )}
            />
          </Row>
          <Row>
            <Fields.Text
              inputProps={{ 'data-testid': `cryptocurrencies[${index}].transactionHash` }}
              type="text"
              label="Transaction Hash (Optional)"
              error={!!errors?.cryptocurrencies?.[index]?.transactionHash}
              helperText={errors?.cryptocurrencies?.[index]?.transactionHash?.message}
              {...register(`cryptocurrencies[${index}].transactionHash`)}
            />
            <Fields.Text
              inputProps={{ 'data-testid': `cryptocurrencies[${index}].notes` }}
              type="text"
              multiline
              rows={2}
              label="Additional Notes (Optional)"
              error={!!errors?.cryptocurrencies?.[index]?.notes}
              helperText={errors?.cryptocurrencies?.[index]?.notes?.message}
              {...register(`cryptocurrencies[${index}].notes`)}
            />
          </Row>
          {fields.length > 1 && (
            <Row justifyContent="flex-end">
              <IconButton onClick={() => remove(index)} size="small" color="error">
                <Icons.Trash fontSize="small" />
              </IconButton>
            </Row>
          )}
        </Stack>
      ))}

      <Button
        type="button"
        disabled={isSubmitting}
        onClick={() =>
          append({
            cryptocurrencyType: '',
            walletAddress: '',
            quantity: 0,
            amount: 0,
            transactionHash: '',
            notes: '',
          })
        }
      >
        <Icons.Plus style={{ marginRight: '10px' }} />
        Add Additional Cryptocurrency
      </Button>
    </Stack>
  )
}

export function Acknowledgement({ control, formState }) {
  const { errors = {} } = formState
  return (
    <>
      <Controller
        control={control}
        rules={{ required: 'You must acknowledge before proceeding.' }}
        name="pageLanguageConfirmed"
        render={({ field: { value, onChange, ...field } }) => (
          <Row justifyContent="space-between" spacing={4} sx={{ marginTop: '2rem!important' }}>
            <Checkbox
              {...field}
              sx={{ marginTop: -1, marginRight: -1 }}
              checked={value}
              onChange={(e) => {
                onChange(e.target.checked)
              }}
            />
            <Text.Body>
              I acknowledge that GiveWise Foundation Canada (GWFC) cannot control cryptocurrency market volatility and
              transfer timing. Cryptocurrency values may fluctuate significantly between initiation and receipt. A tax
              receipt will be issued based on the market value at the time the cryptocurrency is received and
              liquidated. I understand that network fees and conversion costs may be deducted from the donation proceeds
              according to the fee schedule.
            </Text.Body>
          </Row>
        )}
      />
      {errors?.pageLanguageConfirmed && (
        <Text.Body sx={{ color: 'red', marginTop: '0.5rem' }}>{errors.pageLanguageConfirmed.message}</Text.Body>
      )}
    </>
  )
}

/**
 * Fetch Cryptocurrency Transfer Instructions PDF contents
 */
export function fetchGeneratedCryptocurrencyPdf({ me, user, fundId, cryptocurrencies, exchangeInfo, contributionIds }) {
  const { profile } = me ?? {}
  const fundProfile = fundId
    ? me?.funds?.find((f) => f.id === Number(fundId))?.fundProfile
    : me?.funds?.find((f) => !f.name?.startsWith('charity-') && !f.closed)?.fundProfile

  return openApiFileWithData(`/pdf/generate/cryptocurrency-instructions/${contributionIds?.[0]}`, {
    data: {
      donorProfile: {
        firstName: profile?.firstName ?? user?.firstName,
        lastName: profile?.lastName ?? user?.lastName,
        middleInitials: profile?.middleInitials ?? user?.middleInitials,
        phone: profile?.phone ?? user?.phone,
        email: me?.email ?? user?.email,
        address: {
          lineOne: fundProfile?.address?.lineOne ?? user?.address?.lineOne,
          lineTwo: fundProfile?.address?.lineTwo ?? user?.address?.lineTwo,
          city: fundProfile?.address?.city ?? user?.address?.city,
          province: fundProfile?.address?.province ?? user?.address?.province,
          postalCode: fundProfile?.address?.postalCode ?? user?.address?.postalCode,
          country: fundProfile?.address?.country ?? user?.address?.country,
        },
      },
      exchangeName: exchangeInfo?.exchangeName,
      address: exchangeInfo?.address,
      contactEmail: exchangeInfo?.contactEmail,
      contactName: exchangeInfo?.contactName,
      contactPhone: exchangeInfo?.contactPhone,
      cryptocurrencies,
    },
  })
}

export function GeneratedCryptocurrencyInstructions({ cryptocurrencyPdf }) {
  const [fetchedPdf, setFetchedPdf] = useState(null)
  const [isFetching, setIsFetching] = useState(false)

  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  const setAlertRef = useRef()
  useLayoutEffect(() => {
    setAlertRef.current = setAlert
  })
  useEffect(() => {
    const setAlertLocal = setAlertRef.current
    if (!cryptocurrencyPdf) return undefined

    const abortController = new AbortController()

    setFetchedPdf(null)
    setIsFetching(true)
    cryptocurrencyPdf
      .then((pdf) => {
        if (!abortController.signal.aborted) {
          setFetchedPdf(pdf)
        }
      })
      .catch((e) => {
        if (!abortController.signal.aborted) {
          setAlertLocal({
            message: `Failed to create the Cryptocurrency Instructions PDF: ${e.message ?? ''}`,
            error: e.message,
            severity: 'error',
          })
        }
      })
      .finally(() => {
        setIsFetching(false)
      })

    return () => {
      abortController.abort()
    }
  }, [cryptocurrencyPdf])

  return (
    <>
      {isFetching && <p>...generating cryptocurrency transfer instructions...</p>}

      {fetchedPdf && (
        <Link
          href={`data:application/pdf;base64,${fetchedPdf}`}
          download="Cryptocurrency_Transfer_Instructions.pdf"
          target="_blank"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <Icons.FileOpen sx={{ marginRight: '.5rem' }} />
          Cryptocurrency Transfer Instructions (PDF)
        </Link>
      )}

      <Alert {...alertProps} />
    </>
  )
}
